class GlobalWebSocketService {
  private url: string;
  public socket: WebSocket | null;
  private onErrorCallback: ((error: string) => void) | null;
  private onMessageCallback: ((event: MessageEvent) => void) | null;
  private onOpenCallback: (() => void) | null;
  private onCloseCallback: ((event: CloseEvent) => void) | null;
  private shouldReconnect: boolean;
  private hasShownError: boolean = false;

  constructor(url: string) {
    this.url = url;
    this.socket = null;
    this.onErrorCallback = null;
    this.onMessageCallback = null;
    this.onOpenCallback = null;
    this.onCloseCallback = null;
    this.shouldReconnect = true;
  }

  connect(): void {
    if (this.socket) {
      this.disconnect();
    }

    this.socket = new WebSocket(this.url);

    this.socket.onopen = () => {
      this.onOpenCallback?.();
    };

    this.socket.onmessage = (event: MessageEvent) => {
      this.onMessageCallback?.(event);
    };

    this.socket.onerror = (error: Event) => {
      console.error('WebSocket Error:', error);
      this.onErrorCallback?.('WebSocket connection error.');
    };

    this.socket.onclose = (event: CloseEvent) => {
      this.onCloseCallback?.(event);

      if (this.onErrorCallback) {
        this.onErrorCallback('WebSocket connection closed.');
      }
      if (event.code === 1011) {
        this.onErrorCallback?.('Backend crashed unexpectedly.');
      }
      if (this.shouldReconnect && event.code !== 1000) {
        setTimeout(() => this.connect(), 2000);
      }
    };
  }

  setOnErrorCallback(callback: (error: string) => void): void {
    this.onErrorCallback = (error: string) => {
      if (!this.hasShownError) {
        this.hasShownError = true;
        callback(error);
      } else {
        console.warn('Duplicate WebSocket error suppressed:', error);
      }
    };
  }

  setOnMessageCallback(callback: (event: MessageEvent) => void): void {
    this.onMessageCallback = callback;
  }

  setOnOpenCallback(callback: () => void): void {
    this.onOpenCallback = callback;
  }

  setOnCloseCallback(callback: (event: CloseEvent) => void): void {
    this.onCloseCallback = callback;
  }

  sendMessage(message: unknown): void {
    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      try {
        const messageStr =
          typeof message === 'string' ? message : JSON.stringify(message);
        this.socket.send(messageStr);
      } catch {
        this.onErrorCallback?.('Failed to send message');
      }
    } else {
      console.warn('WebSocket is not open. Cannot send message.');
      this.onErrorCallback?.('WebSocket is not connected');
    }
  }

  resetErrorState(): void {
    this.hasShownError = false;
  }

  isConnected(): boolean {
    return this.socket?.readyState === WebSocket.OPEN;
  }

  disconnect(): void {
    this.shouldReconnect = false;

    if (this.socket) {
      this.socket.onclose = null;
      this.socket.onerror = null;
      this.socket.onmessage = null;
      this.socket.onopen = null;

      this.socket.close(1000, 'Client disconnecting');
      this.socket = null;
    }
  }
}

export default GlobalWebSocketService;
